import React, { useState } from 'react';
import { RegulatoryNewsArticle, TOPICS, toDate } from '../../api/regulatoryNews';

const REGULATOR_COLORS: Record<string, { bg: string; color: string }> = {
  FCA: { bg: '#003087', color: '#fff' },
  ESMA: { bg: '#003399', color: '#fff' },
  ASIC: { bg: '#0055a5', color: '#fff' },
  MAS: { bg: '#8c1d40', color: '#fff' },
  CIRO: { bg: '#005c2e', color: '#fff' },
  FSCA: { bg: '#7a4b00', color: '#fff' },
  JFSA: { bg: '#4a1a6e', color: '#fff' },
  CySEC: { bg: '#00695c', color: '#fff' },
};

const TOPIC_LABELS = Object.fromEntries(TOPICS.map((t) => [t.value, t.label]));

const formatDate = (d: Date): string =>
  d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

interface Props {
  article: RegulatoryNewsArticle;
  onHide: (id: string) => Promise<void>;
  onUnhide: (id: string) => Promise<void>;
  canHide: boolean;
}

export const RegulatoryNewsCard: React.FC<Props> = ({ article, onHide, onUnhide, canHide }) => {
  const [busy, setBusy] = useState(false);
  const badge = REGULATOR_COLORS[article.regulator] || { bg: 'var(--c-accent)', color: '#fff' };
  const isNew = Date.now() - toDate(article.ingestedAt).getTime() < ONE_DAY_MS;

  const handleHide = async () => {
    if (!window.confirm('Hide this article from members? You can unhide it later.')) return;
    setBusy(true);
    try {
      await onHide(article.id);
    } finally {
      setBusy(false);
    }
  };

  const handleUnhide = async () => {
    setBusy(true);
    try {
      await onUnhide(article.id);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={article.hidden ? 'rn-card rn-card--hidden' : 'rn-card'}>
      <div className="rn-card-header">
        <span
          className="rn-regulator-badge"
          style={{ background: badge.bg, color: badge.color }}
        >
          {article.regulatorLabel}
        </span>
        {article.topics.map((t) => (
          <span key={t} className="rn-topic-badge">
            {TOPIC_LABELS[t] || t}
          </span>
        ))}
        <span className="rn-date">{formatDate(toDate(article.publishedDate))}</span>
        {isNew && <span className="rn-pending-badge">New</span>}
        {article.hidden && (
          <span className="rn-hidden-badge">
            {!canHide ? 'Not relevant' : article.hiddenReason === 'auto-irrelevant' ? 'Auto-hidden: not relevant' : 'Hidden'}
          </span>
        )}
      </div>

      <h3 className="rn-card-title">{article.title}</h3>

      <p className="rn-summary">{article.summary}</p>

      <div className="rn-why-matters">
        <div className="rn-why-matters-title">Why This Matters</div>
        <p className="rn-why-matters-text">{article.whyThisMatters}</p>
      </div>

      <div className="rn-card-footer">
        <a href={article.sourceUrl} target="_blank" rel="noopener noreferrer" className="rn-source-link">
          View source ↗
        </a>

        {canHide && (
          <div className="rn-review-actions">
            {article.hidden ? (
              <button className="btn btn-outline" disabled={busy} onClick={handleUnhide}>
                {busy ? 'Unhiding…' : 'Unhide'}
              </button>
            ) : (
              <button className="btn btn-outline" disabled={busy} onClick={handleHide}>
                {busy ? 'Hiding…' : 'Hide'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
